/**
 * One roster row: who holds what, when they last signed in, and the one
 * action that takes it back (ORDR-120, FR-STF-003 and FR-STF-004).
 *
 * Replaces ORDR-104's `GrantRow`, which could only show an id prefix and a bare
 * "Revoke". The roster read now carries the holder, so the row names the
 * person, and the button names the person and the role (see `rosterLabels.ts`).
 *
 * LONG ABSENCE IS TEXT, NOT COLOUR. A manager scanning for who has not signed
 * in should not need to perceive a hue; the row carries the words.
 */

import type { ReactElement } from "react";
import { actionLabel } from "./rosterLabels.js";
import { describeSignIn } from "./signIn.js";
import type { RosterEntry } from "./types.js";

export interface RosterRowProps {
  readonly entry: RosterEntry;
  /** From `holderName`, so "You (<name>)" on your own row. */
  readonly holder: string;
  /** The catalogue role name. */
  readonly role: string;
  readonly own: boolean;
  readonly now: Date;
  readonly busy: boolean;
  readonly onRevoke: (entry: RosterEntry) => void;
}

export function RosterRow({
  entry,
  holder,
  role,
  own,
  now,
  busy,
  onRevoke,
}: RosterRowProps): ReactElement {
  const { grant } = entry;
  const signIn = describeSignIn(entry.holder, { now });
  const label = actionLabel("Revoke", holder, role, own);
  return (
    <li
      data-testid={`grant-row-${grant.id}`}
      className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--color-neutral-200)] py-2 text-sm"
    >
      <span className="flex flex-col">
        <span>
          <strong>{holder}</strong>
          {entry.holder?.is_active === false && (
            <span className="ml-2 font-medium">Account deactivated</span>
          )}{" "}
          {role} at {grant.scope_type}:{grant.scope_id}
        </span>
        <span>{signIn.text}</span>
        {signIn.absence !== null && <span className="font-medium">{signIn.absence}</span>}
      </span>
      <button
        type="button"
        disabled={busy}
        onClick={() => onRevoke(entry)}
        data-testid={`revoke-${grant.id}`}
        className="rounded border border-[var(--color-neutral-300)] px-2 py-1 disabled:opacity-60"
      >
        {label}
      </button>
    </li>
  );
}
